import React from 'react';
import _ from 'lodash';
import { v4 as uuid, } from 'uuid';
import PropTypes from 'prop-types';
import SearchBar from '../../searchbar/SearchBar';
import FieldSet from '../../fieldset/FieldSet';
import Grid from '../../grid/Grid';
import PropertyTaxService from '../../../service/PropertyTaxService';
import AsyncActionButton from '../../button/AsyncActionButton';
import DetailedPropertyTaxBill from './DetailedPropertTaxBill';
import AccountLinkForm from '../../form/AccountLinkForm';
import AccountDetailModal from './AccountDetailModal';

class PropertyTaxView extends React.Component {
  constructor(props) {
    super(props);
    const state = {};
    this.comboFields = [
      {
        value: 'customerId',
        display: 'Customer ID',
      },
      {
        value: 'externalAccountId',
        display: 'External Account ID',
      },
    ];
    this.state = state;
  }

  static accountExclusions = [ 'customerIds', 'clientData', 'serviceOfferingId', ];

  static billInclusions = [
    'id',
    'isPaid',
    'taxYear',
    'externalBillId',
    'externalAccountId',
    'payItBalance',
    'clientBillBalance',
    'clientReconciledTimestamp',
    'dueDate',
  ];

  onReset() {
    this.onAccountReset();
    this.onBillReset();
  }

  onAccountReset() {
    this.setState({
      selectedAccount: null,
      accountHeaders: [],
      accountRows: [],
    });
  }

  onBillReset() {
    this.setState({
      selectedBill: null,
      billHeaders: [],
      billRows: [],
    });
  }

  onSearch(event) {
    const { search, combo, } = event;
    const { serviceOfferingId, } = this.props;
    const criteria = {};
    criteria[combo.value] = search.value;
    criteria.serviceOfferingId = serviceOfferingId;
    this.onReset();
    PropertyTaxService.taxAccountSearch(criteria)
      .then((res) => {
        const { accounts, } = res.data;
        const model = Grid.toModelFromList(accounts, {
          headerFormatter: value => _.startCase(value.header),
          filter: value => !PropertyTaxView.accountExclusions.includes(value),
        });
        this.setState({
          accountHeaders: model.headers,
          accountRows: model.format(accounts),
        });
      })
      .finally(() => {
        event.kill();
      });
  }

  onRowSelect(selectedRow) {
    const { accountRows, selectedAccount, } = this.state;
    if (!selectedAccount || selectedRow.id !== selectedAccount.id) {
      this.onBillReset();
      const account = accountRows.find((row) => {
        const { raw, } = row;
        return raw.id === selectedRow.id;
      });
      this.setState({
        selectedAccount: account.raw,
      });
    }
  }

  onBillSelect(selectedRow) {
    const { billRows, selectedBill, } = this.state;
    if (!selectedBill || selectedRow.id !== selectedBill.id) {
      const bill = billRows.find((row) => {
        const { raw, } = row;
        return raw.id === selectedRow.id;
      });
      this.setState({
        selectedBill: bill.raw,
      });
    }
  }

  onServiceOfferingChange(serviceOfferingId) {
    this.setState({
      stateServiceOfferingId: serviceOfferingId,
    });

    this.onAccountReset();
    this.onBillReset();
  }

  getAccountFooter(selectedAccount) {
    const { isLoading, stateServiceOfferingId, } = this.state;
    const { serviceOfferingId, } = this.props;

    if (!selectedAccount) {
      return null;
    }
    return (
      <div className="container mt-2">
        <p>{`Selected ID: ${selectedAccount.id}`}</p>
        <hr />
        <div className="row">
          <div className="m-1">
            <AccountDetailModal selectedAccount={selectedAccount} />
          </div>
          <div className="m-1">
            <AsyncActionButton
              contents="Bills"
              isLoading={isLoading}
              onClick={() => this.getBills({ accountId: selectedAccount.id, serviceOfferingId, })}
            />
          </div>
          <div className="m-1">
            <AccountLinkForm
              customerIds={selectedAccount.customerIds}
              serviceOfferingId={stateServiceOfferingId || serviceOfferingId}
              id={selectedAccount.id}
            />
          </div>
        </div>
      </div>
    );
  }

  getBillFooter(selectedBill) {
    if (!selectedBill) {
      return null;
    }
    return (
      <div className="mt-2">
        <p>{`Selected ID: ${selectedBill.id}`}</p>
        <hr />
        <DetailedPropertyTaxBill bill={selectedBill} />
      </div>
    );
  }

  getBills(criteria) {
    this.setState({ isLoading: true, });
    return PropertyTaxService.getBillByAccountId(criteria)
      .then((res) => {
        this.onBillReset();
        const { bills, } = res.data;
        const model = Grid.toModelFromList(bills, {
          headerFormatter: value => _.startCase(value.header),
          filter: value => PropertyTaxView.billInclusions.includes(value),
        });

        this.setState({
          billHeaders: model.headers,
          billRows: model.format(bills),
        });
      })
      .catch((e) => {
        console.error(e);
      })
      .finally(() => {
        this.setState({ isLoading: false, });
      });
  }

  formatOptions(selectedId, serviceOfferings) {
    const isSelected = value => value === selectedId;
    return (
      <select
        className="form-select mt-2 me-2"
        onBlur={event => this.onServiceOfferingChange(event.currentTarget.value)}
      >
        {serviceOfferings.map(offering => (
          <option key={uuid()} selected={isSelected(offering.value)} value={offering.value}>
            {offering.display}
          </option>
        ))}
      </select>
    );
  }

  render() {
    const {
      accountHeaders,
      accountRows,
      selectedAccount,
      billHeaders,
      billRows,
      selectedBill,
    } = this.state;
    return (
      <div className="container">
        <SearchBar comboFields={this.comboFields} onClick={event => this.onSearch(event)} />
        <div className="mt-2">
          <FieldSet title="Tax Accounts" style={{ maxHeight: '300px', }}>
            <Grid
              headers={accountHeaders}
              rows={accountRows}
              onRowSelect={row => this.onRowSelect(row)}
            />
          </FieldSet>
          {this.getAccountFooter(selectedAccount)}
        </div>
        <div className="mt-2">
          <FieldSet title="Tax Bill">
            <Grid
              headers={billHeaders}
              rows={billRows}
              onRowSelect={data => this.onBillSelect(data)}
            />
          </FieldSet>
          {this.getBillFooter(selectedBill)}
        </div>
      </div>
    );
  }
}

PropertyTaxView.propTypes = {
  serviceOfferingId: PropTypes.string.isRequired,
};

export default PropertyTaxView;
